import $ from 'jquery';
import Flickity from 'flickity';
import 'flickity-imagesloaded';
import layout from '../Layout';
import ProductZoom from './ProductZoom';

export default class ProductImages {
  constructor(el, options) {
    this.el = el;
    this.imagesLayout = options.images_layout;
    this.navigateToImages = options.navigateToImages;
    this.imageLoadCallback = options.imageLoadCallback;

    this.imagesContainer = el.querySelector('[data-product-images]');
    this.images = el.querySelectorAll('[data-product-image]');
    this.imagesCount = this.images.length;

    this.zoomableImages = el.querySelectorAll('[data-product-image-zoom]');

    this.zoomedImages = [];
    for (let i = 0; i < this.zoomableImages.length; i++) {
      this.zoomedImages.push(new ProductZoom(this.zoomableImages[i]));
    }

    this.scrollUpButton = this.el.querySelector('[data-product-images-scroll-up]');
    this.scrollDownButton = this.el.querySelector('[data-product-images-scroll-down]');

    this.onThumbnailClick = this._onThumbnailClick.bind(this);
    this.onScrollClickUp = () => this._scrollVerticalImages('up');
    this.onScrollClickDown = () => this._scrollVerticalImages('down');

    // Handle layout changes
    this.onLayoutChange = this._adjustLayout.bind(this);
    layout.onBreakpointChange(this.onLayoutChange);

    // Initial positioning
    this._adjustLayout();
  }

  setOptions(options) {
    this.navigateToImages = options.navigateToImages;
  }

  // Show correct image for the selected variant
  selectImageByVariant(variant) {
    if (!variant) return;

    if (!variant.featured_image) {
      this.selectImage();
    } else {
      // Grab selected image element
      const image = this.el.querySelector(`[data-product-image-id="${variant.featured_image.id}"]`);

      this.selectImage(image);

      if (this.imagesLayout === 'standard' && !this.flickity) {
        this._highlightImage(image);
      }
    }
  }

  selectImage(image = null) {
    const selectableImage = this.el.querySelector('[data-product-selected-image]');

    // Change slideshow slide if we are in mobile
    if (image && this.flickity) {
      const index = image.getAttribute('data-product-image-index');
      this.flickity.select(index, false, false);
    }

    if (!selectableImage) {
      return;
    }

    if (!image) {
      const defaultId = selectableImage.getAttribute('data-product-selected-image-default');

      image = this.el.querySelector(`[data-product-image-id="${defaultId}"]`);
    }

    for (let i = 0; i < this.images.length; i++) {
      this.images[i].classList.remove('product-image-selected');
    }

    image.classList.add('product-image-selected');

    selectableImage.innerHTML = image.innerHTML;
  }

  unload() {
    if (this.flickity) {
      this._destroyFlickity();
    }

    layout.offBreakpointChange(this.onLayoutChange);

    this.zoomedImages.forEach(zoomedImage => zoomedImage.unload());
  }

  _adjustLayout() {
    if (layout.isBreakpoint('L')) {
      if (this.flickity) {
        this._destroyFlickity();
      }

      if (this.imagesLayout === 'alternate') {
        this._initThumbnails();
      }
    } else if (layout.isBreakpoint('S', 'M')) {
      if (this.imagesLayout === 'alternate') {
        this._destroyThumbnails();
      }

      if (!this.flickity && this.images.length >= 2) {
        this._initFlickity();
      }
    }
  }

  _initFlickity() {
    // Reintroduce Flickity adaptive height (featured in v2.0.0 but not v1.2.1)
    Flickity.prototype.setGallerySize = function setGallerySize() {
      if (this.options.setGallerySize) {
        const height = this.options.adaptiveHeight && this.selectedCell ?
          this.selectedCell.size.outerHeight : this.maxCellHeight;
        this.viewport.style.height = `${height}px`;
      }
    };

    const selectFn = Flickity.prototype.select;
    Flickity.prototype.select = function select(...args) {
      selectFn.apply(this, args);
      if (this.options.adaptiveHeight) {
        this.setGallerySize();
      }
    };

    this.flickity = new Flickity(this.imagesContainer, {
      accessibility: true,
      adaptiveHeight: true,
      cellAlign: 'left',
      cellSelector: '[data-product-image]',
      friction: 0.8,
      imagesLoaded: true,
      pageDots: false,
      prevNextButtons: false,
      selectedAttraction: 0.2,
      wrapAround: true,
    });
  }

  _destroyFlickity() {
    this.flickity.destroy();
    this.flickity = null;

    for (let i = 0; i < this.images.length; i++) {
      this.images[i].setAttribute('style', '');
    }
  }

  _initThumbnails() {
    if (!this.scrollUpButton || !this.scrollDownButton) {
      return;
    }

    this.scrollUpButton.addEventListener('click', this.onScrollClickUp);
    this.scrollDownButton.addEventListener('click', this.onScrollClickDown);

    for (let i = 0; i < this.images.length; i++) {
      this.images[i].addEventListener('click', this.onThumbnailClick);
    }
  }

  _destroyThumbnails() {
    if (!this.scrollUpButton || !this.scrollDownButton) {
      return;
    }

    this.scrollUpButton.removeEventListener('click', this.onScrollClickUp);
    this.scrollDownButton.removeEventListener('click', this.onScrollClickDown);

    for (let i = 0; i < this.images.length; i++) {
      this.images[i].removeEventListener('click', this.onThumbnailClick);
    }
  }

  _onThumbnailClick(event) {
    const image = event.currentTarget;

    this.selectImage(image);
  }

  _scrollVerticalImages(direction) {
    const imagesContainerTop = this.imagesContainer.getBoundingClientRect().top;
    let closestDistance = Infinity;
    let closestIndex = -1;

    for (let i = 0; i < this.images.length; i++) {
      const image = this.images[i];
      const top = image.getBoundingClientRect().top;
      const bottom = image.getBoundingClientRect().bottom;
      const distance = Math.floor(Math.abs(imagesContainerTop - (direction === 'up' ? bottom : top)));

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    }

    const $imagesContainer = $(this.imagesContainer);
    const navigateToImage = direction === 'up'
      ? this.images[Math.max(closestIndex - 1, 0)]
      : this.images[Math.min(closestIndex + 1, this.images.length - 1)];
    const navigateToImageDistance = navigateToImage.getBoundingClientRect().top - imagesContainerTop;

    $imagesContainer.animate({
      scrollTop: this.imagesContainer.scrollTop + navigateToImageDistance,
    }, { queue: false, duration: 500 });
  }

  _highlightImage(image) {
    // Move image to top if we are not in sticky mode
    if (!this.navigateToImages) {
      this.imagesContainer.insertBefore(image, this.images[0]);
      this.images = this.el.querySelectorAll('[data-product-image]');
    }

    // Fade out non-selected images
    for (let i = 0; i < this.images.length; i++) {
      if (image !== this.images[i]) {
        this.images[i].classList.add('product-image-fade');
      }
    }

    // Scroll to selected image
    const scrollTop = image.offsetTop;

    $('html,body').animate({ scrollTop }, 'slow', 'swing', () => {
      // Fade back in all the images
      setTimeout(() => {
        for (let i = 0; i < this.images.length; i++) {
          this.images[i].classList.remove('product-image-fade');
        }
      }, 333);
    });
  }
}
